/**
 * 분할결제 배선 — 세션 결제 화면에 꽂는 유일한 지점 (menu #814)
 *
 * ## 왜 컨테이너를 따로 두는가
 *
 * `SessionChargePage` 는 이미 후불 세션 결제의 전 흐름을 들고 있다. 거기에 좌석 신원
 * 조회·Bill 조회·claim 까지 얹으면, **분할결제가 꺼진 매장의 화면 코드까지** 그 무게를
 * 지게 된다. 훅 배선을 여기 가두면 페이지는 한 줄만 안다.
 *
 * ## 꺼져 있으면 아무것도 그리지 않는다
 *
 * 매장 정책이나 개발 게이트가 닫혀 있으면 `useSessionBills` 가 403 을 받고
 * `isSplitAvailable=false` 가 된다. 그때 이 섹션은 **통째로 사라진다** — 엄브렐라가 못
 * 박은 "기존 UX 무변화" 가 이 한 줄로 지켜진다.
 *
 * ## 좌석 신원은 켜진 세션에서만 확보한다
 *
 * `GET /bills` 는 `X-Comensal-Token` 을 요구하고, 그 토큰은 `scan` 으로만 얻는다.
 * 예전에는 "이 매장이 쓰는지" 를 `/bills` 로만 알 수 있어 **알기 전에 먼저 스캔**해야
 * 했고, 그러면 분할결제를 쓰지도 않는 매장까지 세션마다 참여자 행이 생겼다.
 *
 * admin #2326 이 `split_payment_available`(매장 정책 AND 개발 게이트)을 세션 조회에
 * 실어 주면서 순서가 뒤집혔다 — **켜진 것을 확인한 뒤에만** 스캔한다.
 */
import { useEffect, useState } from 'react';

import { SplitBillPanel, type SplitBillPanelLabels } from './SplitBillPanel';
import { SplitClaimPanel, type SplitClaimPanelLabels } from './SplitClaimPanel';
import { BillReceiptViewer } from './BillReceiptViewer';
import {
  SessionPaymentProgress,
  type SessionPaymentProgressLabels,
} from './SessionPaymentProgress';
import { canClaimShare } from '@/lib/comensalRole';
import { useComensalSession } from '@/hooks/useComensalSession';
import { useSessionBills, type UseSessionBillsReturn } from '@/hooks/useSessionBills';
import type { UseComensalSessionReturn } from '@/hooks/useComensalSession';
import type { Currency } from '@/types';
import { getSplitClaimLabels } from '@/i18n/splitClaims';
import type { Comensal } from '@/lib/api/split-payment';

interface Props {
  /** 테이블 세션 토큰. 없으면 분할결제도 없다. */
  sessionToken: string | null;
  /**
   * 서버가 판정한 가용성(admin #2326). **이것이 참일 때만 좌석 스캔을 한다.**
   *
   * 모를 때 스캔하면 쓰지도 않는 매장에 참여자 행이 남는다.
   */
  isAvailable: boolean;
  currency: Currency;
  labels: SplitPaymentSectionLabels;
  /** 경합(409)·실패를 화면에 알리는 방법. 페이지의 토스트를 그대로 쓴다. */
  onNotify?: (message: string) => void;
  claimLabels?: SplitClaimPanelLabels;
  /** 결제 페이지가 내 Bill quote로 전환하기 위한 서버 권위 scope. */
  onScopeChange?: (scope: SplitPaymentScope) => void;
  /** 상위 결제 페이지가 이미 조회한 상태를 재사용해 중복 current/bills 요청을 막는다. */
  participantState?: Pick<UseComensalSessionReturn, 'deviceToken' | 'comensal'>;
  billsState?: Pick<UseSessionBillsReturn, 'data' | 'isSplitAvailable' | 'claim' | 'isClaiming'>;
  /** 현장/3DS 처리 중인 Bill을 정산 직후 자동으로 영수증까지 이어 준다. */
  autoOpenBillId?: number;
  /** kill-switch 뒤 기존 ledger를 표시만 하고 claim mutation은 닫는다. */
  readOnly?: boolean;
}

export interface SplitPaymentScope {
  isSplitAvailable: boolean;
  deviceToken: string | null;
  comensal: Comensal | null;
  myBillId: number | null;
  remainingMinor: number;
}

export interface SplitPaymentSectionLabels {
  bills: SplitBillPanelLabels;
  progress: SessionPaymentProgressLabels;
  /** 다른 손님이 먼저 가져갔을 때 */
  claimConflict: string;
  claimFailed: string;
}

export function SplitPaymentSection({
  sessionToken,
  isAvailable,
  currency,
  labels,
  onNotify,
  claimLabels = getSplitClaimLabels('es'),
  onScopeChange,
  participantState,
  billsState,
  autoOpenBillId,
  readOnly = false,
}: Props) {
  // 켜진 세션에서만 좌석 신원을 확보한다 — 훅은 조건부로 부를 수 없으므로 토큰을 가려서 넘긴다.
  const scanToken = isAvailable ? sessionToken : null;
  const localParticipant = useComensalSession(participantState === undefined ? scanToken : null);
  const participant = participantState ?? localParticipant;
  const localBills = useSessionBills(
    billsState === undefined ? scanToken : null,
    billsState === undefined ? participant.deviceToken : null,
  );
  const billState = billsState ?? localBills;
  const { deviceToken, comensal } = participant;
  const { data, isSplitAvailable, claim, isClaiming } = billState;
  const [claimMessage, setClaimMessage] = useState<string | null>(null);

  const reportClaimOutcome = (outcome: Awaited<ReturnType<typeof claim>>): void => {
    const message = outcome === 'conflict'
      ? labels.claimConflict
      : outcome === 'error'
        ? labels.claimFailed
        : null;
    setClaimMessage(message);
    if (message !== null) onNotify?.(message);
  };

  useEffect(() => {
    onScopeChange?.({
      isSplitAvailable,
      deviceToken,
      comensal,
      myBillId: data?.my_bill_id ?? null,
      remainingMinor: data?.limits.residual_available_minor ?? 0,
    });
  }, [
    onScopeChange,
    isSplitAvailable,
    deviceToken,
    comensal,
    data?.my_bill_id,
    data?.limits.residual_available_minor,
  ]);

  const canRenderLedger = isSplitAvailable || readOnly;
  if (!canRenderLedger || data === null) {
    return null;
  }

  const canClaim = !readOnly && canClaimShare(comensal?.role, comensal?.join_status);
  const comensalCount = data.participants?.joined_count
    ?? comensal?.session?.joined_count
    ?? Math.max(1, new Set(data.bills.map((bill) => bill.comensal_id).filter(Boolean)).size);

  return (
    <div className="space-y-4" data-testid="split-payment-section">
      {/*
        ★ `bills` 는 **사람별 Bill 만** 담고 잔여는 `residual` 에 따로 온다(스테이징 E2E 로
        확인). 잔여를 빼고 세면, 아무도 아직 가져가지 않은 세션에서 총액이 0 이 되어
        **진행바가 100% 로 거짓말**을 한다 — 아직 아무도 내지 않았는데.
      */}
      <SessionPaymentProgress
        bills={data.residual === null ? data.bills : [...data.bills, data.residual]}
        currency={currency}
        payRemainingMinor={data.limits.residual_available_minor}
        isPayingRemaining={isClaiming}
        onPayRemaining={
          canClaim && data.limits.residual_available_minor > 0
            ? () => {
                setClaimMessage(null);
                void claim({ amount_minor: data.limits.residual_available_minor }).then((outcome) => {
                  reportClaimOutcome(outcome);
                });
              }
            : undefined
        }
        labels={labels.progress}
      />

      {claimMessage !== null ? (
        <p
          className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
          role="alert"
          data-testid="split-payment-claim-alert"
        >
          {claimMessage}
        </p>
      ) : null}

      <SplitBillPanel
        data={data}
        isSplitAvailable={canRenderLedger}
        currency={currency}
        // 등급뿐 아니라 합류 상태도 본다 — 승인 대기 중에 버튼이 열리면 안 된다.
        canClaim={canClaim}
        isClaiming={isClaiming}
        labels={labels.bills}
      />

      {sessionToken !== null && deviceToken !== null && comensal !== null ? (
        <BillReceiptViewer
          sessionToken={sessionToken}
          deviceToken={deviceToken}
          bills={data.bills}
          comensalId={comensal.id}
          autoOpenBillId={autoOpenBillId}
        />
      ) : null}

      {data.residual === null ? (
        <p
          className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
          role="alert"
          data-testid="split-residual-unavailable"
        >
          {claimLabels.unavailable}
        </p>
      ) : (
        <SplitClaimPanel
          items={data.items}
          limits={data.limits}
          comensalCount={comensalCount}
          myBillId={data.my_bill_id}
          canClaim={canClaim}
          isClaiming={isClaiming}
          currency={currency}
          onClaim={async (request) => {
            setClaimMessage(null);
            const outcome = await claim(request);
            reportClaimOutcome(outcome);
            return outcome;
          }}
          onClaimEqual={async (denominator) => {
            setClaimMessage(null);
            const outcome = await claim({ equal_denominator: denominator });
            reportClaimOutcome(outcome);
            return outcome;
          }}
          labels={claimLabels}
        />
      )}
    </div>
  );
}
